import React, { useEffect, useId, useState } from 'react';
import { PlayerAchievementProgress } from '../api';
import './AchievementCard.css';

interface AchievementCardProps {
  achievement: Pick<PlayerAchievementProgress, 'name' | 'description'>;
  isReceived: boolean;
  presentation?: 'collection' | 'game';
}

const getAchievementImagePath = (achievementName: string) => {
  return `/achievements/${encodeURIComponent(achievementName)}.jpg`;
};

const fallbackAchievementImagePath = '/achievements/achievement.jpg';

export const AchievementCard: React.FC<AchievementCardProps> = ({
  achievement,
  isReceived,
  presentation = 'game',
}) => {
  const imagePath = getAchievementImagePath(achievement.name);
  const [currentImagePath, setCurrentImagePath] = useState(imagePath);
  const [isExpanded, setIsExpanded] = useState(false);
  const descriptionId = useId();
  const nameId = useId();

  useEffect(() => {
    setCurrentImagePath(imagePath);
    setIsExpanded(false);
  }, [imagePath]);

  if (presentation === 'collection') {
    return (
      <article className={`achievement-card collection ${isReceived ? 'earned' : 'not-earned'}`}
        role="button" tabIndex={0} aria-labelledby={nameId}
        aria-expanded={isExpanded} aria-controls={descriptionId}
        onClick={() => setIsExpanded(current => !current)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (!event.repeat) setIsExpanded(current => !current);
          }
        }}>
        <div className="achievement-image-frame">
          <img className="achievement-image" src={currentImagePath} alt={achievement.name} loading="lazy"
            onError={() => setCurrentImagePath(fallbackAchievementImagePath)} />
        </div>
        <h3 className="achievement-name" id={nameId}>{achievement.name}</h3>
        <div id={descriptionId} className="achievement-description" hidden={!isExpanded}>
          {achievement.description}
        </div>
      </article>
    );
  }

  return (
    <button
      type="button"
      className={`achievement-card ${isReceived ? 'earned' : 'not-earned'}`}
      title={achievement.description}
      aria-label={achievement.name}
      aria-expanded={isExpanded}
      aria-controls={descriptionId}
      onClick={() => setIsExpanded(current => !current)}
    >
      <div className="achievement-image-frame">
        <img
          className="achievement-image"
          src={currentImagePath}
          alt={achievement.name}
          loading="lazy"
          onError={() => setCurrentImagePath(fallbackAchievementImagePath)}
        />
      </div>
      <div className="achievement-name">{achievement.name}</div>
      {isExpanded && (
        <div id={descriptionId} className="achievement-description">
          {achievement.description}
        </div>
      )}
    </button>
  );
};
